import React from 'react';
import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const EmailInbox = ({
  emails,
  loading,
  selectedEmailUid,
  onSelectEmail,
  searchTerm,
  setSearchTerm,
  page,
  totalPages,
  totalEmails,
  onPageChange,
  onRefresh,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredEmails = emails.filter((email) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      email.from?.toLowerCase().includes(term) ||
      email.fromEmail?.toLowerCase().includes(term) ||
      email.subject?.toLowerCase().includes(term) ||
      email.preview?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <EnvelopeIcon className="h-12 w-12 mb-2" />
            <p>No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredEmails.map((email) => (
              <div
                key={email.uid}
                onClick={() => onSelectEmail(email.uid)}
                className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedEmailUid === email.uid ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                } ${!email.read ? 'bg-white' : 'bg-gray-50'}`}
              >
                <div className="flex items-start gap-3">
                  {/* Read/Unread Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {email.read ? (
                      <EnvelopeOpenIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EnvelopeIcon className="h-5 w-5 text-blue-600" />
                    )}
                  </div>

                  {/* Email Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p
                        className={`text-sm truncate ${
                          !email.read ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {email.from || email.fromEmail}
                      </p>
                      <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                        {formatDate(email.date)}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate ${
                        !email.read ? 'font-medium text-gray-900' : 'text-gray-600'
                      }`}
                    >
                      {email.subject}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500 truncate flex-1">{email.preview}</p>
                      {email.hasAttachments && (
                        <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-700">
            {totalEmails} email{totalEmails !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailInbox;

